/**
 * Легенда сноса для СКАЧАННОЙ картинки — сжатая до строк с метками.
 *
 * Владелец 2026-08-08: «легенду "Дрейф по течениям" необходимо добавлять на
 * картинки при скачивании PNG/MP4». На экране она живёт отдельной складной
 * карточкой слева снизу (`DriftLegend`) и в кадр не попадала: из файла человек
 * получал синие линии, пунктиры, ореолы и точки двух цветов — и ничем их не
 * объяснить.
 *
 * Экранная карточка подробнее: там есть и «наблюдение → сейчас», и маркеры двух
 * дат при перемотке. На плашку берём словарь, без которого КАДР не прочесть:
 * начертание линии (факт/прогноз), цвет точки (косяк/вылов), два особых знака
 * (замёрзший трек и ореол). Остальное — про взаимодействие, а картинка
 * неинтерактивна.
 */
import { t } from '../i18n'
import type { PlateMark } from '../maps/legendRows'
import { DRIFT_SOURCE_COLOR } from './driftMarkerStyle'

/** Заголовок — слово в слово с карточкой на карте: это одна и та же вещь, и
 *  ключ у них общий. */
export const DRIFT_PLATE_TITLE_KEY = 'models.drift.legend.title'

/** Обводка замёрзшего трека — янтарная (как `MARKER_STALE` на карте). */
const STALE_RING = '#f59e0b'
/** Ореол неопределённости — светло-лазурный. */
const HALO_COLOR = '#38bdf8'

export interface DriftPlateLine {
  text: string
  mark?: PlateMark
}

/** Что кабинету показано из фактического вылова (настройка организации). */
export interface DriftLegendVisibility {
  /** Точки вылова скрыты настройкой кабинета, места огрублены (одно состояние). */
  anonymized?: boolean
  /** Ореол погрешности скрыт настройкой кабинета. */
  catchHaloHidden?: boolean
}

/** Строки легенды сноса. `active === false` — снос в кадре не нарисован,
 *  объяснять нечего.
 *
 *  `anonymized` — кабинету закрыты точные места вылова: маркеров вылова в кадре
 *  нет, есть только ореолы. Без строки-объяснения картинка уходит наружу с
 *  пустыми кружками, и это читается как поломка данных.
 *
 *  Скрытые настройкой точки и ореол (заказ владельца 2026-08-13) уносят свои
 *  строки: подпись к тому, чего в кадре нет, — враньё. Про огрубление строка
 *  остаётся — она объясняет круги без точек. Косяков организации это не
 *  касается: их строка остаётся всегда. */
export function driftPlateLines(
  active: boolean,
  visibility: DriftLegendVisibility = {},
): DriftPlateLine[] {
  if (!active) return []
  const { anonymized, catchHaloHidden } = visibility
  const lines: DriftPlateLine[] = [
    { text: t(DRIFT_PLATE_TITLE_KEY) },
    {
      text: t('models.drift.legend.fact'),
      mark: { kind: 'line', color: DRIFT_SOURCE_COLOR.cluster },
    },
    {
      text: t('models.drift.legend.forecast'),
      mark: { kind: 'line', color: DRIFT_SOURCE_COLOR.cluster, dash: true },
    },
    { text: t('models.drift.legend.school'), mark: { kind: 'dot', color: DRIFT_SOURCE_COLOR.cluster } },
  ]
  if (!anonymized) {
    lines.push({
      text: t('models.drift.legend.catch'),
      mark: { kind: 'dot', color: DRIFT_SOURCE_COLOR.catch },
    })
  }
  lines.push({
    text: t('models.drift.legend.lastKnown'),
    mark: { kind: 'dot', color: STALE_RING, ring: STALE_RING, hollow: true },
  })
  if (!catchHaloHidden) {
    lines.push({ text: t('models.drift.legend.halo'), mark: { kind: 'halo', color: HALO_COLOR } })
  }
  // Строка про огрубление объясняет круги БЕЗ точек. Скрыт и ореол — вылова в
  // кадре нет вовсе (бэкенд не отдаёт даже линию), и объяснять нечего.
  if (anonymized && !catchHaloHidden) {
    lines.push({ text: t('models.drift.legend.anonymized') })
  }
  return lines
}
